import { useState } from 'react';

export interface ResizeOptions {
  width?: number;
  height?: number;
  maintainAspectRatio: boolean;
  preset?: 'instagram-square' | 'instagram-portrait' | 'instagram-landscape' | 'facebook-post' | 'twitter-post' | 'youtube-thumbnail' | 'a4-print' | 'letter-print' | 'custom';
  cropMode?: 'center' | 'smart' | 'manual';
  autoCropWhiteSpace?: boolean;
  padding?: number;
  rotation?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
}

interface ProcessedImage {
  id: string;
  original: string;
  processed: string;
  settings: ResizeOptions;
}

const PRESET_SIZES = {
  'instagram-square': { width: 1080, height: 1080 },
  'instagram-portrait': { width: 1080, height: 1350 },
  'instagram-landscape': { width: 1080, height: 566 },
  'facebook-post': { width: 1200, height: 630 },
  'twitter-post': { width: 1200, height: 675 },
  'youtube-thumbnail': { width: 1280, height: 720 },
  'a4-print': { width: 2480, height: 3508 }, // 300 DPI
  'letter-print': { width: 2550, height: 3300 }, // 300 DPI
};

export const useImageResize = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);

  const resizeImage = async (
    imageUrl: string,
    imageId: string,
    options: ResizeOptions
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          let targetWidth = options.width || img.width;
          let targetHeight = options.height || img.height;

          // Apply preset sizes
          if (options.preset && options.preset !== 'custom') {
            const preset = PRESET_SIZES[options.preset];
            targetWidth = preset.width;
            targetHeight = preset.height;
          }

          // Maintain aspect ratio
          if (options.maintainAspectRatio && options.width && options.height) {
            const aspectRatio = img.width / img.height;
            const targetAspectRatio = options.width / options.height;

            if (aspectRatio > targetAspectRatio) {
              targetHeight = Math.round(targetWidth / aspectRatio);
            } else {
              targetWidth = Math.round(targetHeight * aspectRatio);
            }
          }

          // Create canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          // Add padding
          const padding = options.padding || 0;
          canvas.width = targetWidth + padding * 2;
          canvas.height = targetHeight + padding * 2;

          // Fill with white background if padding exists
          if (padding > 0) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          // Save context state
          ctx.save();

          // Calculate center point
          const centerX = canvas.width / 2;
          const centerY = canvas.height / 2;

          // Move to center
          ctx.translate(centerX, centerY);

          // Apply rotation
          if (options.rotation) {
            ctx.rotate((options.rotation * Math.PI) / 180);
          }

          // Apply flipping
          const scaleX = options.flipHorizontal ? -1 : 1;
          const scaleY = options.flipVertical ? -1 : 1;
          ctx.scale(scaleX, scaleY);

          // Draw image centered
          ctx.drawImage(
            img,
            -targetWidth / 2,
            -targetHeight / 2,
            targetWidth,
            targetHeight
          );

          // Restore context
          ctx.restore();

          // Auto-crop white space
          if (options.autoCropWhiteSpace) {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const bounds = getImageBounds(imageData);
            
            if (bounds) {
              const croppedCanvas = document.createElement('canvas');
              const croppedCtx = croppedCanvas.getContext('2d')!;
              croppedCanvas.width = bounds.width;
              croppedCanvas.height = bounds.height;
              croppedCtx.drawImage(
                canvas,
                bounds.x,
                bounds.y,
                bounds.width,
                bounds.height,
                0,
                0,
                bounds.width,
                bounds.height
              );
              resolve(croppedCanvas.toDataURL('image/png'));
              return;
            }
          }

          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const getImageBounds = (imageData: ImageData) => {
    const { data, width, height } = imageData;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Check if pixel is not white and not transparent
        if (!(r > 250 && g > 250 && b > 250) && a > 10) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (minX < maxX && minY < maxY) {
      return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
      };
    }

    return null;
  };

  const processImages = async (
    images: Array<{ id: string; preview: string }>,
    options: ResizeOptions
  ) => {
    setIsProcessing(true);
    setProgress(0);
    const results: ProcessedImage[] = [];

    for (let i = 0; i < images.length; i++) {
      try {
        const processed = await resizeImage(images[i].preview, images[i].id, options);
        results.push({
          id: images[i].id,
          original: images[i].preview,
          processed,
          settings: options,
        });
      } catch (error) {
        console.error(`Failed to process image ${images[i].id}:`, error);
      }
      setProgress(((i + 1) / images.length) * 100);
    }

    setProcessedImages(results);
    setIsProcessing(false);
    return results;
  };

  const downloadProcessed = (image: ProcessedImage, filename: string) => {
    const link = document.createElement('a');
    link.href = image.processed;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = (images: ProcessedImage[], baseFilename: string) => {
    images.forEach((image, index) => {
      const filename = `${baseFilename}_${index + 1}.png`;
      downloadProcessed(image, filename);
    });
  };

  return {
    isProcessing,
    progress,
    processedImages,
    processImages,
    downloadProcessed,
    downloadAll,
  };
};
